import React from 'react';
import { motion } from 'framer-motion';
import { 
  TrendingUp, TrendingDown, DollarSign, Activity, 
  PieChart, BarChart3, Calendar, Download,
  ArrowUpRight, ArrowDownRight, Wallet, Target
} from 'lucide-react';
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart as RePieChart, Pie, Cell, Legend, RadialBarChart,
  RadialBar, ComposedChart
} from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { useQuery } from '@tanstack/react-query';
import { botAPI } from '../services/api';
import { Bot, BotStatus } from '../types/api';
import { cn, formatCurrency, formatPercentage } from '../lib/utils';
import { Skeleton } from './ui/skeleton';

interface PortfolioMetrics {
  totalValue: number;
  totalInvested: number;
  totalPnL: number;
  totalPnLPercentage: number;
  totalPositions: number;
  activePositions: number;
  winRate: number;
  bestPerformer: { symbol: string; pnl: number; percentage: number } | null;
  worstPerformer: { symbol: string; pnl: number; percentage: number } | null;
  dailyPnL: number;
  weeklyPnL: number;
  monthlyPnL: number;
}

interface PositionAllocation {
  symbol: string;
  value: number;
  percentage: number;
  pnl: number;
  pnlPercentage: number;
  is_managed?: boolean;  // True for bot-managed, False for external/unmanaged
}

const Portfolio: React.FC = () => {
  const [timeframe, setTimeframe] = React.useState<'1D' | '1W' | '1M' | '3M' | 'ALL'>('1M');

  // Fetch all bots
  const { data: bots = [], isLoading: botsLoading } = useQuery({
    queryKey: ['bots'],
    queryFn: botAPI.getBots,
  });

  // Fetch bot statuses
  const { data: botStatuses = {} } = useQuery({
    queryKey: ['botStatuses', bots],
    queryFn: async () => {
      if (bots.length === 0) return {};
      
      const statusPromises = bots.map(async (bot) => {
        try {
          const response = await botAPI.getBotStatus(bot.id);
          return { botId: bot.id, status: response.runtime_status };
        } catch (error) {
          return { botId: bot.id, status: null };
        }
      });

      const results = await Promise.all(statusPromises);
      const statusMap: { [key: number]: BotStatus } = {};
      
      results.forEach(({ botId, status }) => {
        if (status) {
          statusMap[botId] = status;
        }
      });

      return statusMap;
    },
    enabled: bots.length > 0,
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  // Calculate portfolio metrics
  const metrics: PortfolioMetrics = React.useMemo(() => {
    let totalValue = 0;
    let totalInvested = 0;
    let totalPnL = 0;
    let activePositions = 0;
    let totalPositions = 0;
    let winningTrades = 0;
    let totalTrades = 0;
    let bestPerformer = null;
    let worstPerformer = null;
    let maxPnLPercentage = -Infinity;
    let minPnLPercentage = Infinity;

    // Aggregate data from all bots
    bots.forEach(bot => {
      const status = botStatuses[bot.id];
      
      // Add initial capital
      totalInvested += bot.config.trading_capital;
      
      if (status) {
        // Add current positions value
        status.current_positions.forEach(position => {
          totalValue += position.current_value;
          totalPnL += position.profit_loss;
          activePositions++;
          totalPositions++;
          
          const pnlPercentage = position.profit_loss_pct;
          if (pnlPercentage > maxPnLPercentage) {
            maxPnLPercentage = pnlPercentage;
            bestPerformer = {
              symbol: position.symbol,
              pnl: position.profit_loss,
              percentage: pnlPercentage
            };
          }
          if (pnlPercentage < minPnLPercentage) {
            minPnLPercentage = pnlPercentage;
            worstPerformer = {
              symbol: position.symbol,
              pnl: position.profit_loss,
              percentage: pnlPercentage
            };
          }
        });
        
        // Count winning trades from history
        status.recent_trades.forEach(trade => {
          if (trade.profit_loss !== undefined) {
            totalTrades++;
            if (trade.profit_loss > 0) {
              winningTrades++;
            }
          }
        });
      }
    });

    // If no positions are open, totalValue equals invested capital
    if (totalValue === 0) {
      totalValue = totalInvested;
    }

    const totalPnLPercentage = totalInvested > 0 ? (totalPnL / totalInvested) * 100 : 0;
    const winRate = totalTrades > 0 ? (winningTrades / totalTrades) * 100 : 0;

    // TODO: Calculate time-based P&L from historical data
    const dailyPnL = totalPnL * 0.1; // Placeholder
    const weeklyPnL = totalPnL * 0.3; // Placeholder
    const monthlyPnL = totalPnL * 0.7; // Placeholder

    return {
      totalValue,
      totalInvested,
      totalPnL,
      totalPnLPercentage,
      totalPositions,
      activePositions,
      winRate,
      bestPerformer,
      worstPerformer,
      dailyPnL,
      weeklyPnL,
      monthlyPnL,
    };
  }, [bots, botStatuses]);

  // Calculate position allocations
  const allocations: PositionAllocation[] = React.useMemo(() => {
    const positions: PositionAllocation[] = [];
    const symbolMap = new Map<string, PositionAllocation>();

    bots.forEach(bot => {
      const status = botStatuses[bot.id];
      if (status) {
        status.current_positions.forEach(position => {
          const existing = symbolMap.get(position.symbol);
          if (existing) {
            existing.value += position.current_value;
            existing.pnl += position.profit_loss;
            // If any position for this symbol is unmanaged, mark the aggregated as unmanaged
            if (position.is_managed === false) {
              existing.is_managed = false;
            }
          } else {
            symbolMap.set(position.symbol, {
              symbol: position.symbol,
              value: position.current_value,
              percentage: 0,
              pnl: position.profit_loss,
              pnlPercentage: position.profit_loss_pct,
              is_managed: position.is_managed !== false,  // Default to true if undefined
            });
          }
        });
      }
    });

    // Calculate percentages
    const totalValue = metrics.totalValue || 1;
    symbolMap.forEach(position => {
      position.percentage = (position.value / totalValue) * 100;
      positions.push(position);
    });

    return positions.sort((a, b) => b.value - a.value);
  }, [bots, botStatuses, metrics.totalValue]);

  // Generate mock historical data for charts
  const generateHistoricalData = () => {
    const days = timeframe === '1D' ? 24 : timeframe === '1W' ? 7 : timeframe === '1M' ? 30 : timeframe === '3M' ? 90 : 365;
    const data = [];
    const baseValue = metrics.totalInvested;
    
    for (let i = 0; i < days; i++) {
      const date = new Date();
      date.setDate(date.getDate() - (days - i));
      const variance = (Math.random() - 0.5) * 0.02 * baseValue;
      const value = baseValue + (metrics.totalPnL / days) * i + variance;
      
      data.push({
        date: date.toISOString().split('T')[0],
        value: value,
        pnl: value - baseValue,
      });
    }
    
    return data;
  };

  const historicalData = generateHistoricalData();

  const pieChartData = allocations.map(allocation => ({
    name: allocation.symbol,
    value: allocation.value,
  }));

  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];

  if (botsLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-6">
          <Skeleton className="h-10 w-64" />
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-32" />
            ))}
          </div>
          <div className="grid gap-6 lg:grid-cols-2">
            <Skeleton className="h-96" />
            <Skeleton className="h-96" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <h1 className="text-3xl font-bold text-foreground">Portfolio Overview</h1>
        <p className="mt-1 text-muted-foreground">
          Track your overall trading performance across all bots
        </p>
      </motion.div>

      {/* Key Metrics */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 mb-8"
      >
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Wallet className="h-4 w-4" />
              Total Portfolio Value
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-bold">{formatCurrency(metrics.totalValue)}</span>
              <Badge 
                variant={metrics.totalPnL >= 0 ? "success" : "destructive"} 
                className="text-xs"
              >
                {metrics.totalPnL >= 0 ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
                {formatPercentage(metrics.totalPnLPercentage)}
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Invested: {formatCurrency(metrics.totalInvested)}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              Total P&L
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-baseline gap-2">
              <span className={cn(
                "text-2xl font-bold",
                metrics.totalPnL >= 0 ? "text-green-500" : "text-red-500"
              )}>
                {formatCurrency(metrics.totalPnL)}
              </span>
            </div>
            <div className="flex gap-4 mt-2 text-xs">
              <span>Day: <span className={cn(metrics.dailyPnL >= 0 ? "text-green-500" : "text-red-500")}>{formatCurrency(metrics.dailyPnL)}</span></span>
              <span>Week: <span className={cn(metrics.weeklyPnL >= 0 ? "text-green-500" : "text-red-500")}>{formatCurrency(metrics.weeklyPnL)}</span></span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Activity className="h-4 w-4" />
              Active Positions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-bold">{metrics.activePositions}</span>
              <span className="text-sm text-muted-foreground">/ {metrics.totalPositions}</span>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Across {bots.length} bots
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Target className="h-4 w-4" />
              Win Rate
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-bold">{metrics.winRate.toFixed(1)}%</span>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Historical performance
            </p>
          </CardContent>
        </Card>
      </motion.div>

      {/* Best & Worst Performers */}
      {(metrics.bestPerformer || metrics.worstPerformer) && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="grid gap-4 md:grid-cols-2 mb-8"
        >
          {metrics.bestPerformer && (
            <Card className="border-green-200 bg-green-50/50 dark:bg-green-900/10">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-green-600" />
                  Best Performer
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <span className="font-medium">{metrics.bestPerformer.symbol}</span>
                  <div className="text-right">
                    <p className="text-green-600 font-medium">
                      {formatCurrency(metrics.bestPerformer.pnl)}
                    </p>
                    <p className="text-xs text-green-600">
                      {formatPercentage(metrics.bestPerformer.percentage)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {metrics.worstPerformer && (
            <Card className="border-red-200 bg-red-50/50 dark:bg-red-900/10">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <TrendingDown className="h-4 w-4 text-red-600" />
                  Worst Performer
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <span className="font-medium">{metrics.worstPerformer.symbol}</span>
                  <div className="text-right">
                    <p className="text-red-600 font-medium">
                      {formatCurrency(metrics.worstPerformer.pnl)}
                    </p>
                    <p className="text-xs text-red-600">
                      {formatPercentage(metrics.worstPerformer.percentage)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </motion.div>
      )}

      {/* Charts */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="grid gap-6 lg:grid-cols-2 mb-8"
      >
        {/* Portfolio Value Chart */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Portfolio Value</CardTitle>
                <CardDescription>Historical performance</CardDescription>
              </div>
              <div className="flex gap-1">
                {(['1D', '1W', '1M', '3M', 'ALL'] as const).map((tf) => (
                  <Button
                    key={tf}
                    variant={timeframe === tf ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setTimeframe(tf)}
                    className="px-2"
                  >
                    {tf}
                  </Button>
                ))}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={historicalData}>
                <defs>
                  <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="date" 
                  tickFormatter={(date) => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                />
                <YAxis tickFormatter={(value) => `$${(value / 1000).toFixed(1)}k`} />
                <Tooltip 
                  formatter={(value: number) => formatCurrency(value)}
                  labelFormatter={(label) => new Date(label).toLocaleDateString()}
                />
                <Area 
                  type="monotone" 
                  dataKey="value" 
                  stroke="#3b82f6" 
                  fillOpacity={1} 
                  fill="url(#colorValue)" 
                />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Allocation Pie Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Portfolio Allocation</CardTitle>
            <CardDescription>By trading pair</CardDescription>
          </CardHeader>
          <CardContent>
            {allocations.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <RePieChart>
                  <Pie
                    data={pieChartData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percentage }) => `${name} ${percentage.toFixed(1)}%`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {pieChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value: number) => formatCurrency(value)} />
                  <Legend />
                </RePieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                No active positions
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* Position Details Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
      >
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Position Details</CardTitle>
                <CardDescription>All active positions across bots</CardDescription>
              </div>
              <Button variant="outline" size="sm">
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {allocations.length > 0 ? (
              <div className="space-y-4">
                {allocations.map((position, index) => (
                  <motion.div
                    key={position.symbol}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="flex items-center justify-between p-4 bg-muted/50 rounded-lg hover:bg-muted/70 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-2 h-8 rounded-full" style={{ backgroundColor: COLORS[index % COLORS.length] }} />
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium">{position.symbol}</p>
                          {position.is_managed === false && (
                            <span className="px-1.5 py-0.5 text-xs bg-yellow-500/20 text-yellow-600 dark:text-yellow-400 rounded-md">
                              Unmanaged
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {position.percentage.toFixed(1)}% of portfolio
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-medium">{formatCurrency(position.value)}</p>
                      <p className={cn(
                        "text-sm font-medium",
                        position.pnl >= 0 ? "text-green-500" : "text-red-500"
                      )}>
                        {formatCurrency(position.pnl)} ({formatPercentage(position.pnlPercentage)})
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                No active positions
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default Portfolio;